/* @flow */
import { Observable } from 'rxjs';
import { combineEpics } from 'redux-observable';
import createDebugger from 'debug';
import { fromJS } from 'immutable';
import { prop } from 'ramda';

import { flags as Flags, getters as Features } from 'modules/features';
import { isCommunityEnclave } from 'modules/enclave/utils';

const debug = createDebugger('app:modules:enclave:epics'); // eslint-disable-line no-unused-vars

import {
  ENCLAVES_ENDPOINT,
  createEnclave,
} from 'modules/enclave/types.js';
import {
  FETCH,
  FETCH_SUCCESS,
  FETCH_FAILURE,
  FETCH_WITH_QUERY,
  FETCH_WITH_QUERY_SUCCESS,
  FETCH_WITH_QUERY_FAILURE,
  SAVE,
  SAVE_SUCCESS,
  SAVE_FAILURE,
  DELETE,
  DELETE_SUCCESS,
  DELETE_FAILURE,
} from './action-types.js';

import {
  SAVE_ENCLAVE_ICON,
} from 'modules/icons/action-types.js';
import {saveEnclaveTemplateIcon} from '../templates/actions';
import {saveEnclaveIcon} from './actions';

export const listToOrderedMap = (list: $TODO) => {
  return fromJS(list)
    .toOrderedMap()
    .map(createEnclave)
    .mapKeys((k, v) => v.get('id'));
};

const formatQueryStringForRequest = (queryObject: Object) => {
  return queryObject ? new URLSearchParams(queryObject).toString() : '';
};

const fetchEpic = (action$, store, { ajax }) =>
  action$.ofType(FETCH, FETCH_WITH_QUERY)
    .mergeMap(({ type, payload }) => {

      // If a payload object is provided, it is a set of queryParams that are to be stringified and attacehd to the request
      return ajax.get(`${ENCLAVES_ENDPOINT}/management${payload ? `?${formatQueryStringForRequest(payload)}` : ''}`)
        .debounceTime(250) // Adding a debouncer so frequent enclave filtering doesn't fire too many requests
        .map(prop('response'))
        .map((enclaves) => {
          const isCommunityEnclaveEnabled = Features.isFeatureEnabled(Flags.COMMUNITY_ENCLAVE_ENABLED)(store.getState());

          return isCommunityEnclaveEnabled
            ? enclaves
            : enclaves.filter((enclave) => !isCommunityEnclave(enclave));
        })
        .map(listToOrderedMap)
        .map(enclaves => ({
          type: type === FETCH_WITH_QUERY ? FETCH_WITH_QUERY_SUCCESS : FETCH_SUCCESS,
          payload: enclaves,
        }))
        .catch(err => Observable.of({
          type: type === FETCH_WITH_QUERY ? FETCH_WITH_QUERY_FAILURE : FETCH_FAILURE,
          error: true,
          payload: err,
        }))});

const saveEnclaveEpic = (action$, store, { ajax }) =>
  action$.ofType(SAVE)
    .mergeMap(({ payload, meta }) => {
      return ajax.postJSON(ENCLAVES_ENDPOINT, payload)
        .map(prop('response'))
        .map(createEnclave)
        .mergeMap(enc => {
          return Observable.of({
            type: SAVE_SUCCESS,
            payload: enc,
            meta: {
              alert: { type: 'success', text: `${payload.get('name')} enclave saved successfully.` },
            },
          });
        })
        .catch(err => {
          return Observable.of({
            type: SAVE_FAILURE,
            meta: {
              alert: { type: 'error', text: `${payload.get('name')} enclave could not be saved. Please reload this page and try again.` },
            },
          });
        });
    });

const saveEnclaveIconEpic = (action$, store, { ajax }) =>
  // first, consume a SAVE event, meaning an enclave is being saved
  action$.ofType(SAVE)
    .switchMap(({ meta }) =>
      // wait until a the enclave is saved successfully
      action$.ofType(SAVE_SUCCESS)
        .take(1)
        .mergeMap(({ payload }) => {

            // dispatch a SAVE_ENCLAVE_ICON event, combining the enclave GUID
            // from the SAVE_SUCCESS event and the file from the SAVE event
            // Note --  SVG and regular icons updated in seperate calls
            const filesToSave = [];
            if (meta.file) filesToSave.push(meta.file);
            if (meta.sourceSVGFile) filesToSave.push(meta.sourceSVGFile);

            return filesToSave.length > 0
              ? filesToSave.map(iconFile => saveEnclaveIcon(payload.id, iconFile))
              : Observable.empty()
          }
        )
        // break the flow if the enclave fails to save
        .takeUntil(action$.ofType(SAVE_FAILURE))
    );

const deleteEnclaveEpic = (action$, store, { ajax }) =>
  action$.ofType(DELETE)
    .mergeMap(({ payload: id }) => {
      return ajax.delete(`${ENCLAVES_ENDPOINT}/${id}`)
        .mergeMap(({ response }) => {
          return Observable.of({
            type: DELETE_SUCCESS,
            payload: id,
            meta: {
              alert: { type: 'success', text: 'Enclave deleted. Companies and users will no longer have access to it.' },
            },
          });
        })
        .catch(err => {
          return Observable.of({
            type: DELETE_FAILURE,
            meta: {
              alert: { type: 'error', text: 'Enclave could not be deleted. Please reload this page and try again' },
            },
          });
        });
    });

export default combineEpics(
  fetchEpic,
  saveEnclaveEpic,
  saveEnclaveIconEpic,
  deleteEnclaveEpic,
);



// WEBPACK FOOTER //
// ./src/app/modules/enclave-management/epics.js